import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useLazyQuery, useApolloClient } from '@apollo/client';
import { CREATE_CATALOG, UPDATE_SELECTED_CATALOG } from '../graphql/mutations';
import { CHECK_CATALOG_NAME_AVAILABILITY, GET_USER_CATALOGS, GET_CURRENT_USER } from '../graphql/queries';
import AuthLayout from './AuthLayout';
import debounce from 'lodash/debounce';
import { useAuth } from '../utils/authContext';
import { validateSubdomainClient, generateSuggestions } from '../utils/reservedSubdomains';

const CatalogSetup = () => {
    const [name, setName] = useState('');
    const [catalogName, setCatalogName] = useState('');
    const [catalogNameError, setCatalogNameError] = useState('');
    const [isCatalogNameAvailable, setIsCatalogNameAvailable] = useState(null);
    const [suggestedCatalogNames, setSuggestedCatalogNames] = useState([]);
    const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);

    const navigate = useNavigate();
    const { refetchUserData } = useAuth(); // Get refetchUserData from useAuth

    const client = useApolloClient();

    const [createCatalog, { loading: createLoading, error: createError }] = useMutation(CREATE_CATALOG, {
        update: (cache, { data: { createCatalog } }) => {
            // Update GET_USER_CATALOGS cache
            try {
                const existingCatalogs = cache.readQuery({
                    query: GET_USER_CATALOGS,
                    variables: { userId: createCatalog.user }
                });

                if (existingCatalogs) {
                    cache.writeQuery({
                        query: GET_USER_CATALOGS,
                        variables: { userId: createCatalog.user },
                        data: {
                            getUserWithCatalogs: {
                                ...existingCatalogs.getUserWithCatalogs,
                                catalogs: [
                                    ...existingCatalogs.getUserWithCatalogs.catalogs,
                                    {
                                        __typename: 'Catalog',
                                        id: createCatalog.id,
                                        catalogName: createCatalog.catalogName,
                                        name: createCatalog.name,
                                        logo: null,
                                        description: null
                                    }
                                ]
                            }
                        }
                    });
                }

                // Update GET_CURRENT_USER cache to include the new selected catalog
                const existingUser = cache.readQuery({
                    query: GET_CURRENT_USER
                });

                if (existingUser) {
                    cache.writeQuery({
                        query: GET_CURRENT_USER,
                        data: {
                            getCurrentUser: {
                                ...existingUser.getCurrentUser,
                                selectedCatalog: {
                                    __typename: 'Catalog',
                                    id: createCatalog.id,
                                    catalogName: createCatalog.catalogName,
                                    name: createCatalog.name,
                                    logo: null,
                                    description: null,
                                    onboardingStatus: 'NOT_STARTED',
                                    onboardingStep: 0,
                                    businessType: null,
                                    businessCategory: null,
                                    subCategories: [],
                                    location: null,
                                    allowCheckout: false,
                                    setupCompleted: false
                                }
                            }
                        }
                    });
                }
            } catch (error) {
                console.error('Error updating cache:', error);
            }
        },
        onCompleted: async (data) => {
            try {
                // Update selected catalog in localStorage
                localStorage.setItem('selectedCatalogId', data.createCatalog.id);

                // Navigate to overview page
                navigate('/overview');
            } catch (error) {
                console.error('Error in onCompleted:', error);
            }
        }
    });


    const [updateSelectedCatalog] = useMutation(UPDATE_SELECTED_CATALOG, {
        update: (cache, { data: { updateSelectedCatalog } }) => {
            if (updateSelectedCatalog) {
                // Clear specific queries from cache to force refetch
                cache.evict({ fieldName: 'catalogItemsAndCategories' });
                cache.evict({ fieldName: 'catalogPerformance' });
                cache.gc();
            }
        }
    });

    const [checkCatalogNameAvailability] = useLazyQuery(CHECK_CATALOG_NAME_AVAILABILITY);

    const handleBack = () => {
        navigate('/profile');
    };

    // Server availability check (only after client validation passes)
    const checkServerAvailability = useCallback(
        debounce(async (name) => {
            setIsCheckingAvailability(true);
            try {
                const { data, error } = await checkCatalogNameAvailability({
                    variables: { catalogName: name.toLowerCase() },
                    errorPolicy: 'all'
                });

                if (error) {
                    console.error('GraphQL error:', error);
                    setIsCatalogNameAvailable(false);
                    setCatalogNameError('Error checking availability. Please try again.');
                    return;
                }

                if (data && typeof data.checkCatalogNameAvailability === 'boolean') {
                    setIsCatalogNameAvailable(data.checkCatalogNameAvailability);
                    if (!data.checkCatalogNameAvailability) {
                        const suggestions = generateSuggestions(name);
                        setSuggestedCatalogNames(suggestions);
                        setCatalogNameError('This catalog name is already taken');
                    } else {
                        setSuggestedCatalogNames([]);
                        setCatalogNameError('');
                    }
                } else {
                    setIsCatalogNameAvailable(false);
                    setCatalogNameError('Error checking availability');
                }
            } catch (error) {
                console.error('Error checking catalog name availability:', error);
                setIsCatalogNameAvailable(false);
                setCatalogNameError('Network error. Please try again.');
            } finally {
                setIsCheckingAvailability(false);
            }
        }, 1000), // 1 second debounce to reduce server calls
        [checkCatalogNameAvailability]
    );


    const handleCatalogNameChange = (e) => {
        const newCatalogName = e.target.value.toLowerCase().replace(/\s+/g, '');
        setCatalogName(newCatalogName);

        // Reset states
        setIsCatalogNameAvailable(null);
        setSuggestedCatalogNames([]);

        // Client-side validation first (no server call)
        const clientValidation = validateSubdomainClient(newCatalogName);

        if (!clientValidation.isValid) {
            setCatalogNameError(clientValidation.error);
            if (clientValidation.error.includes('reserved')) {
                const suggestions = generateSuggestions(newCatalogName);
                setSuggestedCatalogNames(suggestions);
            }
            return;
        }

        // Only make server call if client validation passes
        setCatalogNameError('');
        checkServerAvailability(newCatalogName);
    };

    const handleSuggestionClick = (suggestedName) => {
        setCatalogName(suggestedName);
        setCatalogNameError('');
        setSuggestedCatalogNames([]);
        setIsCatalogNameAvailable(null);

        // Check availability of the suggested name
        const clientValidation = validateSubdomainClient(suggestedName);
        if (clientValidation.isValid) {
            checkServerAvailability(suggestedName);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Final validation before submission
        const clientValidation = validateSubdomainClient(catalogName);
        if (!clientValidation.isValid) {
            setCatalogNameError(clientValidation.error);
            return;
        }

        if (isCatalogNameAvailable === false) {
            setCatalogNameError('Please choose an available catalog name');
            return;
        }

        const token = localStorage.getItem('accessToken' || 'refreshToken');
        if (!token) {
            console.error('No authentication token found');
            return;
        }

        try {
            await createCatalog({
                variables: {
                    name: name,
                    catalogName: catalogName.toLowerCase()
                },
                optimisticResponse: {
                    createCatalog: {
                        __typename: 'Catalog',
                        id: 'temp-id-' + Date.now(),
                        name: name,
                        catalogName: catalogName.toLowerCase(),
                        user: localStorage.getItem('userId'),
                        onboardingStatus: 'NOT_STARTED',
                        onboardingStep: 0
                    }
                }
            });
        } catch (error) {
            console.error('Error creating catalog:', error);

            // Handle specific error cases with suggestions
            if (error.message.includes('reserved') || error.message.includes('already in use')) {
                setCatalogNameError(error.message);
                // Parse suggestions from server error if available
                const suggestionMatch = error.message.match(/Try these alternatives: (.+)/);
                if (suggestionMatch) {
                    const suggestions = suggestionMatch[1].split(', ');
                    setSuggestedCatalogNames(suggestions);
                } else {
                    setSuggestedCatalogNames(generateSuggestions(catalogName));
                }
            } else if (error.graphQLErrors) {
                error.graphQLErrors.forEach(({ message }) => {
                    console.error(message);
                });
            } else if (error.networkError) {
                console.error('Network error. Please check your connection.');
            } else {
                console.error('An unexpected error occurred.');
            }
        }
    };

    // Validation icon
    const getValidationIcon = () => {
        if (isCheckingAvailability) {
            return (
                <svg className="animate-spin h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
            );
        }

        if (isCatalogNameAvailable === true) {
            return (
                <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
            );
        }

        if (isCatalogNameAvailable === false) {
            return (
                <svg className="h-4 w-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
            );
        }

        return null;
    };

    return (
        <AuthLayout
            title="Choose Your Catalog URL"
            description="This will be your unique store link on pricilog.com"
            imageSrc="/banner1.svg"
        >
            <div className="flex justify-end mb-5">
                <button
                    onClick={handleBack}
                    className="text-[#191970] text-sm bg-gray-50 hover:underline flex items-center"
                >
                    <svg className="w-3 h-3 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back to Profile
                </button>
            </div>

            <form onSubmit={handleSubmit}>
                <div className="mb-6">
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Store Name</label>
                    <input
                        type="text"
                        id="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Mary's Bakery"
                        className="w-full px-3 py-2 mb-5 border bg-white text-black border-gray-300 rounded-md focus:outline-none focus:ring-[#2424db] focus:border-[#2424db]"
                        required
                    />
                    <label htmlFor="catalogName" className="block text-sm font-medium text-gray-700 mb-1">Store Link</label>
                    <div className="mt-1">
                        <div className="relative bg-white border border-gray-300 rounded-lg  transition-all duration-200">
                            <div className="flex items-center p-3">
                                <span className="text-gray-500 text-base font-medium whitespace-nowrap">
                                    https://
                                </span>
                                <input
                                    type="text"
                                    id="catalogName"
                                    className="flex-1 mx-1 px-2 py-1 text-base text-[#191970] bg-yellow-50 border border-yellow-200 rounded focus:outline-none focus:bg-white focus:border-[#2424db] transition-all duration-200"
                                    placeholder="marysbakery"
                                    value={catalogName}
                                    onChange={handleCatalogNameChange}
                                />
                                <span className="text-gray-500 text-base font-medium whitespace-nowrap">
                                    .pricilog.com
                                </span>
                                {(isCheckingAvailability || isCatalogNameAvailable !== null) && (
                                    <span className="ml-3 flex items-center">
                                        {getValidationIcon()}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Live Preview */}
                        {catalogName && !catalogNameError && (
                            <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
                                <p className="text-sm text-green-700">
                                    ✓ Your store will be live at:
                                    <span className="font-semibold ml-1">https://{catalogName}.pricilog.com</span>
                                </p>
                            </div>
                        )}
                    </div>
                    {catalogNameError && <p className="mt-2 text-sm text-red-600">{catalogNameError}</p>}

                    <p className="text-sm text-gray-500 mt-2">
                        Choose a unique link for your store. Use only letters, numbers, and hyphens (3-32 characters).
                    </p>
                    {/* {!catalogNameError && catalogName && (
                        <p className="mt-2 text-xs text-gray-500">
                            Your catalog will be accessible at{' '}
                            <span className='text-[#191970] font-medium'>
                                https://{catalogName}.pricilog.com
                            </span>
                        </p>
                    )} */}
                </div>

                {suggestedCatalogNames.length > 0 && (
                    <div className="mb-6 p-4 bg-blue-50 rounded-md">
                        <p className="text-sm font-medium text-gray-700 mb-2">
                            {catalogNameError?.includes('reserved') ? 'This name is reserved. Try these alternatives:' : 'Suggested alternatives:'}
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {suggestedCatalogNames.map((suggestedName, index) => (
                                <button
                                    key={index}
                                    type="button"
                                    className="px-3 py-1 text-sm bg-white border border-[#191970] text-[#191970] rounded-md hover:bg-[#191970] hover:text-white transition-colors"
                                    onClick={() => handleSuggestionClick(suggestedName)}
                                >
                                    {suggestedName}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <button
                    type="submit"
                    className="w-full bg-[#191970] text-white py-2 px-4 rounded-md hover:bg-opacity-90 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={!isCatalogNameAvailable || !!catalogNameError || createLoading || isCheckingAvailability}
                >
                    {createLoading ? 'Creating Catalog...' : 'Create Catalog'}
                </button>

                {createError && <p className="mt-4 text-sm text-red-600">{createError.message}</p>}
            </form>
        </AuthLayout>
    );
};

export default CatalogSetup;